import { Component } from '@theme/component';
import { track } from '@theme/analytics';

// Mailchimp's embedded-form endpoint sends no CORS headers, so the background
// submit has to be JSONP: `/subscribe/post` becomes `/subscribe/post-json` and
// the answer arrives as a call to the function named in `c`.
const JSONP_PATH_SUFFIX = '/subscribe/post';
const JSONP_HOST_SUFFIX = '.list-manage.com';
const JSONP_TIMEOUT_MS = 10000;
/** Mailchimp prefixes field errors with the field's index: "3 - Please enter a value". */
const FIELD_PREFIX = /^(\d+)\s+-\s+/;

let callbackCount = 0;

/**
 * Signup form for a Mailchimp audience (background request, no page reload).
 *
 * The form markup is a complete Mailchimp embedded form — action URL, merge
 * tags, marketing-permission field, tags and honeypot all come from the block
 * settings — so it also works without JavaScript, where it posts to Mailchimp's
 * hosted confirmation page. This component keeps the visitor on the page
 * instead and shows Mailchimp's answer inline.
 *
 * Mailchimp is only contacted on submit; nothing loads from it before.
 * Whether a confirmation mail is sent is controlled by the audience's double
 * opt-in setting: with it on, a subscriber only shows up in Mailchimp after
 * confirming.
 *
 * @typedef {object} MailchimpSignupRefs
 * @property {HTMLFormElement} form
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} errorText
 * @property {HTMLSelectElement} [countrySelect]
 *
 * @typedef {{ result?: string, msg?: string }} MailchimpResponse
 *
 * @extends {Component<MailchimpSignupRefs>}
 */
class MailchimpSignup extends Component {
  requiredRefs = ['form', 'submitButton', 'successMessage', 'errorMessage', 'errorText'];

  connectedCallback() {
    super.connectedCallback();
    this.#localizeCountries();
  }

  /**
   * The country options arrive with English names. The browser knows every
   * country in the page language, so the names are swapped here and the list is
   * sorted again — no country translations live in the theme. The values stay
   * the ISO codes Mailchimp receives.
   */
  #localizeCountries() {
    const select = this.refs.countrySelect;
    const language = document.documentElement.lang || 'en';
    if (!select || language.toLowerCase().startsWith('en') || typeof Intl.DisplayNames !== 'function') return;

    let names;
    try {
      names = new Intl.DisplayNames([language], { type: 'region' });
    } catch (error) {
      // An unknown language tag: the English names stay.
      return;
    }

    const options = Array.from(select.options).filter((option) => option.value);
    for (const option of options) {
      const name = names.of(option.value);
      if (name && name !== option.value) option.textContent = name;
    }
    options.sort((a, b) => (a.textContent || '').localeCompare(b.textContent || '', language));
    // Appending moves the options into the new order; the placeholder stays first.
    select.append(...options);
  }

  /**
   * Handles the form submission. Native validation (required fields, email
   * format, both consent checkboxes) has already passed when this fires. The
   * disabled submit button provides single-flight semantics for the request.
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const { form, submitButton } = this.refs;

    submitButton.disabled = true;
    this.#setState('idle');

    try {
      const response = await this.#subscribe(form);

      if (response.result === 'success') {
        form.reset();
        this.#setState('success');
        this.#trackSignup();
      } else {
        const message = readableMessage(response.msg);
        console.warn('[mailchimp-signup] Mailchimp rejected the signup:', message);
        this.#trackError('rejected', fieldIndex(response.msg));
        this.#setState('error', message);
      }
    } catch (error) {
      // Surface the cause (e.g. a wrong action URL) so a misconfiguration is
      // distinguishable from a network failure.
      console.error('[mailchimp-signup]', error);
      this.#trackError('request_failed');
      this.#setState('error');
    } finally {
      submitButton.disabled = false;
    }
  };

  /**
   * @param {HTMLFormElement} form
   * @returns {Promise<MailchimpResponse>}
   */
  #subscribe(form) {
    // getAttribute, not form.action: a field named "action" would shadow the property.
    const endpoint = new URL(form.getAttribute('action') || '');

    // The URL ends up as a script source, so it must not leave Mailchimp.
    if (
      endpoint.protocol !== 'https:' ||
      !endpoint.hostname.endsWith(JSONP_HOST_SUFFIX) ||
      !endpoint.pathname.endsWith(JSONP_PATH_SUFFIX)
    ) {
      throw new Error(`Not a Mailchimp form action: ${endpoint.href}`);
    }
    endpoint.pathname = `${endpoint.pathname}-json`;

    for (const [name, value] of new FormData(form)) {
      if (typeof value === 'string') endpoint.searchParams.append(name, value);
    }

    return requestJsonp(endpoint);
  }

  /**
   * Fires only once Mailchimp has accepted the signup — never on the click.
   * Without a form name in the block settings no event is sent.
   */
  #trackSignup() {
    const formName = this.dataset.formName;
    if (formName) track('email_signup', { form_name: formName });
  }

  /**
   * Makes failed signups countable: without it a broken field mapping (ECR-357)
   * only shows up when someone complains. Mailchimp's text can contain the
   * email address, so only the type and the field index leave the browser.
   * @param {'rejected' | 'request_failed'} errorType
   * @param {string} [field] - index of the field Mailchimp rejected
   */
  #trackError(errorType, field) {
    const formName = this.dataset.formName;
    if (formName) {
      track('email_signup_error', { form_name: formName, error_type: errorType, error_field: field || 'none' });
    }
  }

  /**
   * @param {'idle' | 'success' | 'error'} state
   * @param {string} [message] - Mailchimp's reason for an error, if it gave one
   */
  #setState(state, message) {
    const { successMessage, errorMessage, errorText } = this.refs;

    if (state === 'error') {
      errorText.textContent = message || errorText.dataset.fallback || '';
    }

    successMessage.hidden = state !== 'success';
    errorMessage.hidden = state !== 'error';
    if (state === 'success') successMessage.focus();
    if (state === 'error') errorMessage.focus();
  }
}

/**
 * @param {URL} endpoint
 * @returns {Promise<MailchimpResponse>}
 */
function requestJsonp(endpoint) {
  return new Promise((resolve, reject) => {
    const callbackName = `jmMailchimpCallback${Date.now()}${callbackCount++}`;
    const script = document.createElement('script');
    /** @type {Record<string, unknown>} */
    const globals = /** @type {any} */ (window);

    const timer = setTimeout(() => {
      // Leave a no-op behind: an answer arriving after the timeout would
      // otherwise call an undefined function.
      globals[callbackName] = () => delete globals[callbackName];
      script.remove();
      reject(new Error('Mailchimp request timed out'));
    }, JSONP_TIMEOUT_MS);

    globals[callbackName] = (/** @type {MailchimpResponse} */ response) => {
      clearTimeout(timer);
      delete globals[callbackName];
      script.remove();
      resolve(response || {});
    };

    script.onerror = () => {
      clearTimeout(timer);
      delete globals[callbackName];
      script.remove();
      reject(new Error('Mailchimp request failed'));
    };

    endpoint.searchParams.set('c', callbackName);
    script.src = endpoint.href;
    document.head.append(script);
  });
}

/**
 * Mailchimp's messages are HTML and, for field errors, start with the index of
 * the offending field ("0 - Please enter a value"). The text is shown as plain
 * text: the markup comes from a third party and is not injected into the page.
 * @param {string} [message]
 * @returns {string}
 */
function readableMessage(message) {
  return plainText(message).replace(FIELD_PREFIX, '').trim();
}

/**
 * @param {string} [message]
 * @returns {string | undefined} the field index of a field error, e.g. "3"
 */
function fieldIndex(message) {
  return FIELD_PREFIX.exec(plainText(message))?.[1];
}

/**
 * @param {string} [message]
 * @returns {string}
 */
function plainText(message) {
  if (!message) return '';
  return new DOMParser().parseFromString(message, 'text/html').body.textContent || '';
}

if (!customElements.get('mailchimp-signup')) {
  customElements.define('mailchimp-signup', MailchimpSignup);
}
